import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import throttle from 'lodash/throttle'

import { setRedirect } from '../../../appActions'
import { RootState } from '../../../appReducer'
import { SearchBar } from '../../shared/custom-fields/SearchBar'
import { fetchPractice } from '../../practices/actions'
import AppointmentSettingsPracticeHeader from '../../shared/AppointmentSettingsPracticeHeader'

import AppointmentSettingsLocationsList from './AppointmentSettingsLocationsList'
import {
    fetchConnectedPracticeLocations,
    fetchPracticeLocations,
    fetchPracticeLocationSchedulingConfiguration,
    searchPracticeLocationsRequest,
} from './v2actions'

import './AppointmentSettings.sass'

export type AppointmentSettingsRouteParams = RouteComponentProps<{
    practiceId: string
}>

type Props = AppointmentSettingsRouteParams

const moduleName = 'appointment-settings'

const AppointmentSettings = (props: Props) => {
    const practiceId = props.match.params.practiceId

    const page = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.page || 1,
    )

    const [searchPage, setSearchPage] = useState<number>(page)

    const [firstListLoadDone, setFirstListLoadDone] = useState<boolean>(false)
    const [searchKeyword, setSearchKeyword] = useState<string>('')
    const [hasSearch, setHasSearch] = useState<boolean>(false)

    const practice = useSelector((state: RootState) => state.practices.practices[practiceId])
    const allPages = useSelector(
        (state: RootState) =>
            state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.pagination?.allPages || 0,
    )
    const list = useSelector(
        (state: RootState) => state.v2.reschedulingConfiguration.practiceLocationsData?.[practiceId]?.list,
    )

    const practiceFetched = practice?.id

    const dispatch = useDispatch()

    useEffect(() => {
        return () => {
            clearSearch()
            dispatch(searchPracticeLocationsRequest(practiceId, 1, false))
        }
    }, [dispatch, practiceId])

    useEffect(() => {
        if (!practiceFetched) {
            dispatch(fetchPractice(practiceId))
        }
    }, [practiceFetched, practiceId, dispatch])

    useEffect(() => {
        dispatch(fetchPracticeLocationSchedulingConfiguration(practiceId))
        dispatch(fetchConnectedPracticeLocations(practiceId))
    }, [practiceFetched, practiceId, dispatch])

    const onReturnToPractices = () => {
        dispatch(setRedirect(`/practices`))
    }

    const searchForPracticeLocations = useCallback(
        (searchKeyword: string, searchPage: number) => {
            dispatch(fetchPracticeLocations(practiceId, searchKeyword, searchPage))
        },
        [dispatch, practiceId],
    )

    const throttledSearchForPracticeLocations = useMemo(() => throttle(searchForPracticeLocations, 300), [
        searchForPracticeLocations,
    ])

    useEffect(() => {
        throttledSearchForPracticeLocations(searchKeyword.trim().toLocaleLowerCase(), searchPage)
    }, [searchKeyword, searchPage, throttledSearchForPracticeLocations])

    useEffect(() => {
        if ((allPages > 1 || searchKeyword.trim() !== '') && !firstListLoadDone) {
            setHasSearch(true)
            setFirstListLoadDone(true)
        }
    }, [allPages, searchKeyword, firstListLoadDone])

    const searchByKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(e.target.value || '')
        setSearchPage(1)
    }
    const clearSearch = () => {
        setSearchKeyword('')
    }

    if (!practice) {
        return (
            <div className="circular-progress-loader">
                <CircularProgress size={100} color="primary" variant="indeterminate" />
            </div>
        )
    }

    return (
        <div className={moduleName}>
            <div className="detached-button" onClick={onReturnToPractices}>
                <i className="material-icons">arrow_back</i>
            </div>
            <AppointmentSettingsPracticeHeader practice={practice} />
            <div className={`${moduleName}__title`}>Appointment Settings</div>
            <div className={`${moduleName}__subtitle-wrapper`}>
                <div className={`${moduleName}__subtitle`}>
                    Control how patients can book, reschedule, or cancel appointments, and manage availability settings.
                </div>
            </div>
            {hasSearch && (
                <div className="search-bar-container">
                    <SearchBar
                        value={searchKeyword}
                        onChange={searchByKeyword}
                        onClear={clearSearch}
                        isMessageShown={searchKeyword.length > 0 && !list?.length}
                        noResultsMessage="No records found for given name or ID of Facility ID"
                        placeholder="Search by Location Name or Location ID or Facility ID"
                    />
                </div>
            )}

            <AppointmentSettingsLocationsList
                searchPage={searchPage}
                setSearchPage={setSearchPage}
                practiceId={practiceId}
                isPending={true}
            />
        </div>
    )
}

export default AppointmentSettings
